// Pure helpers for the Blocks Explorer. Engine semantics mirror data-science-API block_eval.py.

export const CUSTOMER_TYPES = ['NEW', 'KNOWN', 'REACT']

const BLOCK_TABLES = ['numeric', 'categorical']

const ACTION_FAMILIES = {
  hard: [1, 3],
  conditional_hard: [4, 5],
  reduced_bid: [2],
  extra_document: [6],
  soft: [0],
}

export const BLOCK_FILTER_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'hard', label: 'Hard' },
  { key: 'conditional_hard', label: 'Conditional hard' },
  { key: 'reduced_bid', label: 'Reduced bid' },
  { key: 'extra_document', label: 'Extra doc' },
  { key: 'soft', label: 'Soft' },
  { key: 'overridden_here', label: 'Overridden here' },
  { key: 'findings', label: 'Has findings' },
]

// Columns the engine never selects, so editing them changes nothing in underwriting.
export const NOT_READ_BY_UNDERWRITING = {
  numeric: ['place_datetime', 'release_datetime', 'pre_uw', 'post_buy'],
  categorical: [
    'set_date',
    'release_date',
    'is_compliance',
    'post_buy',
    'type',
  ],
}

// Numeric bounds are strict: a value exactly on min or max does not trip.
export function describeRule({ table, row, conditions = [] }) {
  if (table === 'numeric') {
    const attribute = row.attribute ?? '(no attribute)'
    const hasMin = row.min !== null && row.min !== undefined
    const hasMax = row.max !== null && row.max !== undefined
    if (hasMin && hasMax) return `${attribute} < ${row.min} or > ${row.max}`
    if (hasMin) return `${attribute} < ${row.min}`
    if (hasMax) return `${attribute} > ${row.max}`
    return 'Never trips (no bounds)'
  }

  if (conditions.length === 0) return 'Never trips (no conditions)'
  return conditions
    .map((condition) => `${condition.field_name} = "${condition.field_value}"`)
    .join(' AND ')
}

export function actionTagClass(action) {
  if (ACTION_FAMILIES.hard.includes(action)) return 'is-danger'
  if (ACTION_FAMILIES.conditional_hard.includes(action)) return 'is-warning'
  if (ACTION_FAMILIES.reduced_bid.includes(action)) return 'is-info'
  if (ACTION_FAMILIES.extra_document.includes(action)) return 'is-link'
  if (ACTION_FAMILIES.soft.includes(action)) return 'is-light'
  return 'is-dark'
}

function matchesChip(explorerRow, chip, treatmentId) {
  const { block, findings } = explorerRow
  if (chip === 'all') return true
  if (chip === 'overridden_here') {
    return block.is_override && block.source_treatment_id === treatmentId
  }
  if (chip === 'findings') return findings.length > 0
  return (ACTION_FAMILIES[chip] ?? []).includes(block.row.block_action)
}

function matchesQuery(explorerRow, query) {
  const needle = query.trim().toLowerCase()
  if (needle === '') return true
  const { row, conditions } = explorerRow.block
  const haystack = [
    `#${row.id}`,
    row.name,
    row.attribute,
    ...conditions.flatMap((condition) => [
      condition.field_name,
      condition.field_value,
    ]),
  ]
  return haystack.some(
    (value) =>
      value !== null &&
      value !== undefined &&
      String(value).toLowerCase().includes(needle)
  )
}

export function filterBlocks(explorerRows, { query, chip, treatmentId }) {
  return explorerRows.filter(
    (explorerRow) =>
      matchesChip(explorerRow, chip, treatmentId) &&
      matchesQuery(explorerRow, query)
  )
}

export function sourceLabel(treatmentId, treatmentNamesById) {
  if (treatmentId === 0) return 'Base'
  return treatmentNamesById[treatmentId] ?? `Treatment ${treatmentId}`
}

export function readUrlState(searchParams) {
  const treatment = searchParams.get('treatment')
  const customer = searchParams.get('customer')
  const changeRequest = searchParams.get('cr')
  const [table, id] = (searchParams.get('block') ?? '').split(':')
  const changeRequestId = /^[1-9]\d*$/.test(changeRequest ?? '')
    ? Number(changeRequest)
    : null
  // A negative id is a row a draft creates, so it only exists while that draft is viewed.
  const idPattern = changeRequestId === null ? /^\d+$/ : /^-?\d+$/

  return {
    treatmentId: /^\d+$/.test(treatment ?? '') ? Number(treatment) : null,
    customerType: CUSTOMER_TYPES.includes(customer) ? customer : 'NEW',
    query: searchParams.get('q') ?? '',
    block:
      BLOCK_TABLES.includes(table) && idPattern.test(id ?? '')
        ? { table, id: Number(id) }
        : null,
    changeRequestId,
  }
}

export function buildUrlState({
  treatmentId,
  customerType,
  query,
  block,
  changeRequestId,
}) {
  const params = {}
  if (treatmentId !== null && treatmentId !== undefined) {
    params.treatment = String(treatmentId)
  }
  params.customer = customerType
  if (query) params.q = query
  if (block) params.block = `${block.table}:${block.id}`
  if (changeRequestId) params.cr = String(changeRequestId)
  return params
}

// Counted over the rows the search leaves, so each chip shows what clicking it would list.
export function chipCounts(explorerRows, { query, treatmentId }) {
  const searched = explorerRows.filter((row) => matchesQuery(row, query))
  const countFor = (key) =>
    searched.filter((row) => matchesChip(row, key, treatmentId)).length

  return Object.fromEntries(
    BLOCK_FILTER_CHIPS.map(({ key }) => [key, countFor(key)])
  )
}

const SORT_VALUES = {
  id: (explorerRow) => explorerRow.block.row.id,
  name: (explorerRow) => explorerRow.block.row.name?.toLowerCase() ?? null,
  rule: (explorerRow) =>
    describeRule({
      table: explorerRow.table,
      row: explorerRow.block.row,
      conditions: explorerRow.block.conditions,
    }).toLowerCase(),
  action: (explorerRow) => explorerRow.block.row.block_action,
  customer: (explorerRow) => explorerRow.block.row.customer_type,
}

// Missing values sort last in both directions; equal values keep the API order.
export function sortBlocks(
  explorerRows,
  { key, direction = 'asc', treatmentNamesById = {} }
) {
  const valueOf =
    key === 'source'
      ? (explorerRow) =>
          sourceLabel(
            explorerRow.block.source_treatment_id,
            treatmentNamesById
          ).toLowerCase()
      : SORT_VALUES[key]
  if (!valueOf) return explorerRows

  const sign = direction === 'desc' ? -1 : 1
  return [...explorerRows].sort((a, b) => {
    const left = valueOf(a)
    const right = valueOf(b)
    if (left === right) return 0
    if (left === null || left === undefined) return 1
    if (right === null || right === undefined) return -1
    return (left < right ? -1 : 1) * sign
  })
}

export function paginate(rows, { page, perPage }) {
  const totalPages = Math.max(1, Math.ceil(rows.length / perPage))
  const current = Math.min(Math.max(1, page), totalPages)
  return {
    pageRows: rows.slice((current - 1) * perPage, current * perPage),
    page: current,
    totalPages,
  }
}

export function pageNumbers({ page, totalPages }) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1)
  }
  const range = (from, to) =>
    Array.from({ length: to - from + 1 }, (_, index) => from + index)
  if (page <= 4) return [...range(1, 5), '…', totalPages]
  if (page >= totalPages - 3) {
    return [1, '…', ...range(totalPages - 4, totalPages)]
  }
  return [1, '…', page - 1, page, page + 1, '…', totalPages]
}

// The shared Pagination reads a Laravel paginator's meta; client-side paging builds one.
export function pagerMeta({ page, totalPages }) {
  const linkTo = (number) => `?page=${number}`
  const toLink = (number) => {
    if (number === '…') return { url: null, label: '...', active: false }
    return {
      url: linkTo(number),
      label: String(number),
      active: number === page,
    }
  }
  const numbered = pageNumbers({ page, totalPages }).map(toLink)
  return {
    current_page: page,
    last_page: totalPages,
    path: '',
    links: [
      {
        url: page > 1 ? linkTo(page - 1) : null,
        label: '&laquo; Previous',
        active: false,
      },
      ...numbered,
      {
        url: page < totalPages ? linkTo(page + 1) : null,
        label: 'Next &raquo;',
        active: false,
      },
    ],
  }
}

export function pageFromUrl(url) {
  const query = (url ?? '').split('?')[1] ?? ''
  const page = Number(new URLSearchParams(query).get('page'))
  return Number.isInteger(page) && page > 0 ? page : 1
}

// KNOWN never matches a block (leads are KNOWN, blocks are not), so it is flagged rather than coloured.
const CUSTOMER_TAG_CLASSES = {
  ALL: 'is-link is-light',
  NEW: 'is-info is-light',
  REACT: 'is-warning is-light',
}

export function customerTagClass(customerType) {
  return CUSTOMER_TAG_CLASSES[customerType] ?? 'is-danger is-light'
}

export function filterTree(nodes, query) {
  const needle = query.trim().toLowerCase()
  if (needle === '') return nodes
  return nodes.flatMap((node) => {
    const children = filterTree(node.children, query)
    const matches = (node.name ?? '').toLowerCase().includes(needle)
    return matches || children.length > 0 ? [{ ...node, children }] : []
  })
}

export function treatmentPath(nodes, treatmentId) {
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index]
    if (node.id === treatmentId) return [node]
    const below = treatmentPath(node.children, treatmentId)
    if (below.length > 0) return [node, ...below]
  }
  return []
}
